import React, { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Lock, Eye, EyeOff, Loader2, Check, AlertCircle } from "lucide-react";
import Logo from "../components/Logo";
import { BACKEND_URL } from "../lib/config";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const errorMessage = useMemo(() => {
    if (!token) return "Link di reset mancante o non valido.";
    return error;
  }, [token, error]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 4) {
      setError("La password deve essere di almeno 4 caratteri.");
      return;
    }
    if (pwd !== pwd2) {
      setError("Le due password non coincidono.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token,
        password: pwd,
      });
      setDone(true);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Errore durante il reset. Il link potrebbe essere scaduto.";
      setError(typeof msg === "string" ? msg : "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex justify-center">
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(700px 400px at 50% -10%, rgba(20,194,161,0.35) 0%, rgba(20,194,161,0) 60%), #141818",
        }}
      />
      <div className="w-full max-w-[430px] min-h-screen bg-[#2b3134] flex flex-col">
        <div className="pt-10 pb-6 flex flex-col items-center gap-3">
          <Logo />
          <div className="text-white/60 text-[12px] uppercase tracking-[0.25em]">
            Nuova password
          </div>
        </div>
        <div className="px-6 flex-1">
          {done ? (
            <div
              className="rounded-2xl bg-[#14c2a1]/12 border border-[#14c2a1]/30 p-5 text-center"
              data-testid="reset-done-state"
            >
              <div className="w-12 h-12 rounded-full bg-[#14c2a1]/25 flex items-center justify-center mx-auto">
                <Check size={22} className="text-[#67e8d1]" strokeWidth={3} />
              </div>
              <h3 className="mt-3 text-white text-[18px] font-semibold">
                Password aggiornata
              </h3>
              <p className="mt-2 text-white/75 text-[13.5px] leading-relaxed">
                La tua password è stata cambiata correttamente. Ora puoi
                accedere con le nuove credenziali.
              </p>
              <button
                onClick={() => navigate("/login", { replace: true })}
                data-testid="reset-go-login"
                className="mt-5 w-full h-12 rounded-2xl text-white font-semibold text-[15px]"
                style={{
                  background:
                    "linear-gradient(155deg, #12b096 0%, #0a8670 100%)",
                  boxShadow: "0 14px 30px -12px rgba(20,194,161,0.55)",
                }}
              >
                Vai al login
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-white text-[22px] font-semibold mb-2">
                Imposta una nuova password
              </h2>
              <p className="text-white/65 text-[13.5px] leading-relaxed mb-5">
                Scegli una nuova password per il tuo account SmartDream.
                Minimo 4 caratteri.
              </p>
              <form onSubmit={submit} className="space-y-3">
                <PwdRow
                  value={pwd}
                  onChange={setPwd}
                  show={showPwd}
                  setShow={setShowPwd}
                  placeholder="Nuova password"
                  testid="reset-pwd-1"
                />
                <PwdRow
                  value={pwd2}
                  onChange={setPwd2}
                  show={showPwd}
                  setShow={setShowPwd}
                  placeholder="Conferma password"
                  testid="reset-pwd-2"
                />
                {errorMessage && (
                  <div
                    className="text-rose-300 text-sm bg-rose-500/10 border border-rose-400/20 rounded-lg px-3 py-2 flex items-start gap-2"
                    data-testid="reset-error"
                  >
                    <AlertCircle size={14} className="mt-0.5 shrink-0" />
                    <span>{errorMessage}</span>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading || !token}
                  data-testid="reset-submit"
                  className="w-full h-12 rounded-2xl text-white font-semibold text-[15px] flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{
                    background:
                      "linear-gradient(155deg, #12b096 0%, #0a8670 100%)",
                    boxShadow: "0 14px 30px -12px rgba(20,194,161,0.55)",
                  }}
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  Salva nuova password
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PwdRow({ value, onChange, show, setShow, placeholder, testid }) {
  return (
    <div className="flex items-center h-12 px-4 gap-3 rounded-2xl bg-white/5 border border-white/10 focus-within:border-[#14c2a1] focus-within:ring-2 focus-within:ring-[#14c2a1]/25 transition-all">
      <Lock size={16} className="text-white/50" />
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="flex-1 bg-transparent text-white text-sm placeholder-white/40 focus:outline-none"
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="text-white/60 hover:text-white"
        aria-label="toggle"
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}
